import { useMemo, useState } from 'react';
import {
  BookOpen,
  House,
  Map,
  MessageCircle,
  Rocket,
  Settings,
  ShoppingCart,
  TrendingUp,
  Zap,
} from 'lucide-react';
import {
  DescriptionText,
  HorizontalStack,
  RegistryEmptyState,
  SilkInput,
  Surface,
  VerticalStack,
} from '@workspace/silk-ui';

import { Button } from '@/components/ui/button';
import { useNavigation } from '@/context/NavigationContext';
import { buildPortalHelpRoute, portalHelpSearchTerms } from '@/lib/portal-help';
import type { PortalHelpArticleCategory } from '@/lib/portal-help';
import { getPortalHelpArticle, getPortalHelpArticles, getPortalHelpArticlesByCategory } from '@/views/help/portalHelpContent';

type CategoryConfig = {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
};

const categoryConfig: Record<PortalHelpArticleCategory, CategoryConfig> = {
  'getting-started': {
    label: 'Getting Started',
    description: 'Orient yourself in the tenant-first portal and understand the starting workflow.',
    icon: Rocket,
  },
  'tenant-workspaces': {
    label: 'Tenant Workspaces',
    description: 'Understand portal home, tenant context, and secondary account compatibility pages.',
    icon: House,
  },
  support: {
    label: 'Support',
    description: 'Find the current support stub, help guidance, and next-step paths while tickets stay retired.',
    icon: MessageCircle,
  },
  maps: {
    label: 'Maps',
    description: 'Configure map sources, styles, custom maps, sprites, and glyphs.',
    icon: Map,
  },
  sales: {
    label: 'Sales',
    description: 'Explore sales geography and territory-oriented workflows.',
    icon: TrendingUp,
  },
  commerce: {
    label: 'Commerce',
    description: 'Set up catalogs, pricing, channels, markets, and order flows.',
    icon: ShoppingCart,
  },
  automation: {
    label: 'Automation',
    description: 'Access API gateway routes, the developer console, and automation surfaces.',
    icon: Zap,
  },
  settings: {
    label: 'Settings',
    description: 'Adjust your personal portal appearance and shell preferences.',
    icon: Settings,
  },
};

const featuredArticleIds = ['getting-started', 'portal-home', 'support', 'tenant-context'];

export default function PortalHelpCenterPage() {
  const navigation = useNavigation();
  const [query, setQuery] = useState('');
  const articles = useMemo(() => getPortalHelpArticles(), []);

  const filteredArticles = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return articles;
    return articles.filter(article =>
      portalHelpSearchTerms(article).join(' ').toLowerCase().includes(term)
    );
  }, [articles, query]);

  const isSearching = query.trim().length > 0;

  const featuredArticles = useMemo(() => {
    if (isSearching) return [];
    return featuredArticleIds
      .map(id => getPortalHelpArticle(id))
      .filter((a): a is NonNullable<typeof a> => a !== null);
  }, [isSearching]);

  const groupedArticles = useMemo(() => {
    const groups = getPortalHelpArticlesByCategory();
    return Object.entries(groups)
      .map(([category, entries]) => ({
        category: category as PortalHelpArticleCategory,
        config: categoryConfig[category as PortalHelpArticleCategory] ?? { label: category, description: '', icon: BookOpen },
        articles: entries.filter(article =>
          filteredArticles.some(c => c.articleId === article.articleId)
        ),
      }))
      .filter(group => group.articles.length > 0);
  }, [filteredArticles]);

  const handleKeywordClick = (keyword: string) => {
    setQuery(keyword);
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6 md:px-6">
      {/* Header */}
      <Surface className="border-border/60 p-6">
        <VerticalStack gap={5}>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">Help Center</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-foreground">How can we help?</h1>
            <DescriptionText className="mt-3 max-w-3xl text-sm leading-6 text-foreground/80">
              Search by question, keyword, or workflow — or browse categories below.
            </DescriptionText>
          </div>
          <SilkInput
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Try: switch tenant, support, setup, API gateway, catalogs..."
            aria-label="Search help"
          />
          {!isSearching && (
            <div className="flex flex-wrap gap-2">
              {(Object.entries(categoryConfig) as [PortalHelpArticleCategory, CategoryConfig][]).map(
                ([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setQuery(config.label)}
                      className="flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-semibold text-foreground/70 transition-colors hover:border-border hover:bg-muted/30 hover:text-foreground"
                    >
                      <Icon className="h-3 w-3" />
                      {config.label}
                    </button>
                  );
                }
              )}
            </div>
          )}
        </VerticalStack>
      </Surface>

      {/* Featured articles */}
      {!isSearching && featuredArticles.length > 0 && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-black tracking-tight text-foreground">Popular Topics</h2>
            <DescriptionText className="text-xs">Start here if you're new or need quick answers.</DescriptionText>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {featuredArticles.map(article => {
              const config = categoryConfig[article.category];
              const Icon = config?.icon ?? BookOpen;
              return (
                <button
                  key={article.articleId}
                  type="button"
                  onClick={() => navigation.navigate(buildPortalHelpRoute(article.articleId))}
                  className="group text-left"
                >
                  <Surface className="h-full border-border/60 p-5 transition-all hover:border-border hover:shadow-md group-focus-visible:ring-2 group-focus-visible:ring-ring">
                    <VerticalStack gap={3}>
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted/40">
                        <Icon className="h-4 w-4 text-foreground/60" />
                      </div>
                      <div>
                        <p className="text-sm font-black text-foreground">{article.title}</p>
                        <DescriptionText className="mt-1 text-xs leading-5">{article.summary}</DescriptionText>
                      </div>
                    </VerticalStack>
                  </Surface>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Empty state */}
      {groupedArticles.length === 0 && (
        <Surface className="border-border/60 p-6">
          <RegistryEmptyState
            icon={BookOpen}
            title="No matching help topics"
            description="Try a broader term or clear the search to browse all help categories."
          />
        </Surface>
      )}

      {/* Category groups */}
      {groupedArticles.map(group => {
        const Icon = group.config.icon;
        return (
          <section key={group.category} className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted/40">
                <Icon className="h-3.5 w-3.5 text-foreground/60" />
              </div>
              <div>
                <h2 className="text-lg font-black tracking-tight text-foreground">{group.config.label}</h2>
                <DescriptionText className="text-xs">{group.config.description}</DescriptionText>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {group.articles.map(article => (
                <button
                  key={article.articleId}
                  type="button"
                  onClick={() => navigation.navigate(buildPortalHelpRoute(article.articleId))}
                  className="group text-left"
                >
                  <Surface className="h-full border-border/60 p-5 transition-all hover:border-border hover:shadow-md group-focus-visible:ring-2 group-focus-visible:ring-ring">
                    <VerticalStack gap={3}>
                      <div>
                        <h3 className="text-sm font-black text-foreground">{article.title}</h3>
                        <DescriptionText className="mt-1.5 text-xs leading-5 text-foreground/80">
                          {article.summary}
                        </DescriptionText>
                      </div>
                      {article.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {article.keywords.slice(0, 3).map(keyword => (
                            <span
                              key={keyword}
                              role="button"
                              tabIndex={0}
                              onClick={e => {
                                e.stopPropagation();
                                handleKeywordClick(keyword);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.stopPropagation();
                                  handleKeywordClick(keyword);
                                }
                              }}
                              className="cursor-pointer rounded border border-border/60 bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:border-border hover:text-foreground"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      )}
                    </VerticalStack>
                  </Surface>
                </button>
              ))}
            </div>
          </section>
        );
      })}

      {/* Footer */}
      <Surface className="border-border/60 p-5">
        <HorizontalStack gap={4} wrap className="items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-sm font-black text-foreground">Still have questions?</p>
              <DescriptionText className="text-xs">
                If you still need guidance, open the support stub and continue from there.
              </DescriptionText>
            </div>
          </div>
          <Button type="button" variant="outline" onClick={() => navigation.navigate('/support')}>
            Contact Support
          </Button>
        </HorizontalStack>
      </Surface>
    </div>
  );
}
